import React, { useState } from 'react';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridActionsCellItem,
  GridRowParams,
} from '@mui/x-data-grid';
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Block as BlockIcon,
  CheckCircle as UnblockIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { User, UserFilters } from '../types/api';
import { usersApi } from '../services/api';
import UserDetailsModal from './UserDetailsModal';
// Убираем date-fns пока что
// import { format } from 'date-fns';
// import { ru } from 'date-fns/locale';

interface UsersTableProps {
  users: User[];
  total: number;
  loading: boolean;
  filters: UserFilters;
  onFiltersChange: (filters: UserFilters) => void;
}

export default function UsersTable({
  users,
  total,
  loading,
  filters,
  onFiltersChange,
}: UsersTableProps) {
  const [blockDialog, setBlockDialog] = useState<{
    open: boolean;
    user: User | null;
    action: 'block' | 'unblock';
  }>({ open: false, user: null, action: 'block' });
  
  const [detailsModal, setDetailsModal] = useState<{
    open: boolean;
    user: User | null;
  }>({ open: false, user: null });

  const queryClient = useQueryClient();

  // Мутация для блокировки/разблокировки пользователя
  const blockMutation = useMutation({
    mutationFn: ({ userId, action }: { userId: number; action: 'block' | 'unblock' }) =>
      action === 'block' ? usersApi.blockUser(userId) : usersApi.unblockUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setBlockDialog({ open: false, user: null, action: 'block' });
    },
  });

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Не указано';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getSubscriptionColor = (subscription: string) => {
    switch (subscription) {
      case 'premium': return 'primary';
      case 'advanced': return 'secondary';
      case 'basic': return 'success';
      case 'False': return 'default';
      default: return 'default';
    }
  };

  const handleBlockAction = (user: User, action: 'block' | 'unblock') => {
    setBlockDialog({ open: true, user, action });
  };

  const confirmBlockAction = () => {
    if (blockDialog.user) {
      blockMutation.mutate({
        userId: blockDialog.user.id,
        action: blockDialog.action,
      });
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
    },
    {
      field: 'telegram_id',
      headerName: 'Telegram ID',
      width: 120,
      renderCell: (params) => (
        <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: 'username',
      headerName: 'Username',
      width: 150,
      renderCell: (params) => (
        <Box>
          {params.value ? `@${params.value}` : 'Не указано'}
        </Box>
      ),
    },
    {
      field: 'full_name',
      headerName: 'Имя',
      width: 200,
      valueGetter: (params) => {
        const { first_name, last_name } = params.row;
        return [first_name, last_name].filter(Boolean).join(' ') || 'Не указано';
      },
    },
    {
      field: 'subscription',
      headerName: 'Подписка',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value === 'False' ? 'Нет' : params.value}
          color={getSubscriptionColor(params.value)}
          size="small"
          variant={params.value === 'False' ? 'outlined' : 'filled'}
        />
      ),
    },
    {
      field: 'user_language',
      headerName: 'Язык',
      width: 80,
      renderCell: (params) => (
        <Box sx={{ textTransform: 'uppercase', fontWeight: 500 }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: 'audio_uses',
      headerName: 'Аудио',
      width: 80,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'gpt_uses',
      headerName: 'GPT',
      width: 80,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'is_bot_blocked',
      headerName: 'Статус',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Заблокирован' : 'Активен'}
          color={params.value ? 'error' : 'success'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Регистрация',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ fontSize: '0.875rem' }}>
          {formatDate(params.value)}
        </Box>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Действия',
      width: 120,
      getActions: (params: GridRowParams<User>) => [
        <GridActionsCellItem
          key="view"
          icon={
            <Tooltip title="Просмотреть детали">
              <ViewIcon />
            </Tooltip>
          }
          label="Просмотреть"
          onClick={() => setDetailsModal({ open: true, user: params.row })}
        />,
        <GridActionsCellItem
          key="block"
          icon={
            <Tooltip title={params.row.is_bot_blocked ? "Разблокировать" : "Заблокировать"}>
              {params.row.is_bot_blocked ? <UnblockIcon /> : <BlockIcon />}
            </Tooltip>
          }
          label={params.row.is_bot_blocked ? "Разблокировать" : "Заблокировать"}
          onClick={() => handleBlockAction(params.row, params.row.is_bot_blocked ? 'unblock' : 'block')}
        />,
      ],
    },
  ];

  const handlePaginationChange = (model: GridPaginationModel) => {
    onFiltersChange({
      ...filters,
      page: model.page + 1, // DataGrid использует 0-based индексы
      size: model.pageSize,
    });
  };

  return (
    <>
      <DataGrid
        rows={users}
        columns={columns}
        loading={loading}
        pageSizeOptions={[10, 20, 50, 100]}
        pagination
        paginationMode="server"
        rowCount={total}
        paginationModel={{
          page: (filters.page || 1) - 1, // DataGrid использует 0-based индексы
          pageSize: filters.size || 20,
        }}
        onPaginationModelChange={handlePaginationChange}
        disableRowSelectionOnClick
        sx={{
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
        localeText={{
          // Локализация
          noRowsLabel: 'Нет данных',
          noResultsOverlayLabel: 'Результаты не найдены',
          toolbarDensity: 'Плотность',
          toolbarDensityLabel: 'Плотность',
          toolbarDensityCompact: 'Компактная',
          toolbarDensityStandard: 'Стандартная',
          toolbarDensityComfortable: 'Комфортная',
          toolbarColumns: 'Столбцы',
          toolbarColumnsLabel: 'Выбрать столбцы',
          toolbarFilters: 'Фильтры',
          toolbarFiltersLabel: 'Показать фильтры',
          toolbarFiltersTooltipHide: 'Скрыть фильтры',
          toolbarFiltersTooltipShow: 'Показать фильтры',
          toolbarExport: 'Экспорт',
          columnsPanelTextFieldLabel: 'Найти столбец',
          columnsPanelTextFieldPlaceholder: 'Заголовок столбца',
          columnsPanelShowAllButton: 'Показать все',
          columnsPanelHideAllButton: 'Скрыть все',
          MuiTablePagination: {
            labelRowsPerPage: 'Строк на странице:',
            labelDisplayedRows: ({ from, to, count }) =>
              `${from}–${to} из ${count !== -1 ? count : `более чем ${to}`}`,
          },
        }}
      />

      {/* Диалог подтверждения блокировки/разблокировки */}
      <Dialog
        open={blockDialog.open}
        onClose={() => setBlockDialog({ open: false, user: null, action: 'block' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {blockDialog.action === 'block' ? 'Заблокировать пользователя' : 'Разблокировать пользователя'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {blockDialog.action === 'block' 
              ? `Вы уверены, что хотите заблокировать пользователя ${blockDialog.user?.first_name || 'ID: ' + blockDialog.user?.id}?`
              : `Вы уверены, что хотите разблокировать пользователя ${blockDialog.user?.first_name || 'ID: ' + blockDialog.user?.id}?`
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setBlockDialog({ open: false, user: null, action: 'block' })}
            color="inherit"
          >
            Отмена
          </Button>
          <Button
            onClick={confirmBlockAction}
            color={blockDialog.action === 'block' ? 'error' : 'success'}
            variant="contained"
            disabled={blockMutation.isPending}
          >
            {blockMutation.isPending 
              ? 'Обработка...' 
              : blockDialog.action === 'block' ? 'Заблокировать' : 'Разблокировать'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Модальное окно с деталями пользователя */}
      <UserDetailsModal
        user={detailsModal.user}
        open={detailsModal.open}
        onClose={() => setDetailsModal({ open: false, user: null })}
      />
    </>
  );
}